import { Core } from "@faire/design-tokens";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { PerStyleVariationMapBuilder } from "@faire/retailer/features/OpenSizing/PerStyleVariationMapBuilder";
import { QuantityPicker } from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/QuantityPicker";
import { SizeChartLink } from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/SizeChartLink";

interface IProps {
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
  perStyleOptionVariation: PerStyleVariationMapBuilder.IPerStyleOptionVariation[];
  minimumOrderQuantity: number;
  perStyleTotalQuantity: number;
  updateSelectedQuantityByToken: (token: string, quantity: number) => void;
}

const OpenSizingVariantTableBase: React.FC<IProps> = ({
  brand,
  product,
  perStyleOptionVariation,
  minimumOrderQuantity,
  perStyleTotalQuantity,
  updateSelectedQuantityByToken,
}) => {
  return (
    <Table>
      <Row justify="space-between">
        <Column>
          <Row>
            <Typography>
              <StrictLocalMsg
                defaultMessage="Sizes"
                description={{
                  text: "You can select sizes for this product variant here",
                }}
              />
            </Typography>
            <Spacer width="1x" />
            <SizeChartLink brand={brand} product={product} />
          </Row>
        </Column>
        <Column>
          <Typography data-test-id="open-sizing-table-moq">
            <StrictLocalMsg
              defaultMessage="Min Qty {minimumOrderQuantity}"
              values={DEPRECATED_backwardsCompatibleValues({
                minimumOrderQuantity,
              })}
              description={{
                text: "The minimum quantity of {minimumOrderQuantity} must be selected to place an order",
              }}
            />
          </Typography>
        </Column>
      </Row>
      <Spacer height={6} />
      {perStyleOptionVariation.map((variation, i) => (
        <QuantityPicker
          key={variation.size}
          label={variation.size}
          onChange={(value: number) => {
            updateSelectedQuantityByToken(
              variation.productOptionToken,
              Math.max(value, 0)
            );
          }}
          value={variation.selectedQuantity}
          quantityThreshold={variation.inventory}
          isFirst={i === 0}
          isDisabled={variation.inventory === 0}
          costSuffix={variation.wholesalePrice}
          promoPrice={variation.promoPrice}
          shipDate={
            variation.showDatesInQuanitityPicker
              ? variation.expectedShipDate
              : undefined
          }
          orderByDate={
            variation.showDatesInQuanitityPicker
              ? variation.orderByDate
              : undefined
          }
        />
      ))}
      <TotalRow>
        <Row align="center">
          <Spacer width="2x" />
          <Typography>
            <StrictLocalMsg
              defaultMessage="Total"
              description={{ text: "Total quantity selected" }}
            />
          </Typography>
        </Row>
        <Row align="center">
          <QuantityContainer data-test-id="open-sizing-total-quantity">
            <Typography>{perStyleTotalQuantity}</Typography>
          </QuantityContainer>
        </Row>
      </TotalRow>
      <Spacer size="3x" />
    </Table>
  );
};

export const OpenSizingVariantTable = observer(OpenSizingVariantTableBase);

const Table = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TotalRow = styled(Row).attrs({
  justify: "space-between",
  align: "center",
})`
  width: 100%;
  height: 50px;
  margin-top: -1px;
  border: 1px solid ${Core.border.muted};
`;

const QuantityContainer = styled(Row).attrs({
  justify: "center",
})`
  /* A little out of protocol, but it is to match
    the same width as the quantity pickers
    to align properly.
   */
  width: 150px;
`;
